import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import {
  DRUG_REGIMENS,
  getSideEffectsForRegimen,
  initialState,
} from '../constants/adrData';

const PatientFormContext = createContext(null);

export function usePatientForm() {
  const ctx = useContext(PatientFormContext);
  if (!ctx) {
    throw new Error('usePatientForm must be used within PatientFormProvider');
  }
  return ctx;
}

export function hasRequiredPersonalInfo(form) {
  return !!(
    form.name?.trim() &&
    form.age?.toString().trim() &&
    form.gender?.trim() &&
    form.bloodGroup?.trim() &&
    form.phone?.trim() &&
    form.regNo?.trim() &&
    form.address?.trim()
  );
}

function mapPatientToForm(patient) {
  return {
    ...initialState,
    ...patient,
    sideEffects: Array.isArray(patient.sideEffects)
      ? patient.sideEffects
      : patient.sideEffects
        ? [patient.sideEffects]
        : [],
    sideEffectCycles: patient.sideEffectCycles || {},
  };
}

export function PatientFormProvider({ children, patient, onSubmit }) {
  const [consent, setConsent] = useState(patient ? true : null);
  const [form, setForm] = useState(() => (patient ? mapPatientToForm(patient) : initialState));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (patient) {
      setConsent(true);
      setForm(mapPatientToForm(patient));
      setErrors({});
      return;
    }

    setConsent(null);
    setForm(initialState);
    setErrors({});
  }, [patient]);

  const selectedDrugRegimens = DRUG_REGIMENS[form.diagnosis] || [];
  const selectedSideEffects = useMemo(
    () => getSideEffectsForRegimen(form.drugRegimen),
    [form.drugRegimen]
  );

  const handleChange = useCallback((e) => {
    const { name, value, type, checked, selectedOptions, dataset } = e.target;

    if (
      name === 'sideEffectCycle' ||
      name === 'sideEffectCycleOther' ||
      name === 'sideEffectFrequency' ||
      name === 'sideEffectRemedy' ||
      name === 'sideEffectRemedyOther' ||
      name === 'sideEffectReduced'
    ) {
      const effect = dataset.effect;

      setForm((prev) => {
        const current = prev.sideEffectCycles?.[effect] || {};

        return {
          ...prev,
          sideEffectCycles: {
            ...prev.sideEffectCycles,
            [effect]: {
              ...current,
              ...(name === 'sideEffectCycle'
                ? { cycle: value, other: value === 'Other' ? current.other || '' : '' }
                : name === 'sideEffectCycleOther'
                  ? { other: value }
                  : name === 'sideEffectFrequency'
                    ? { frequency: value }
                    : name === 'sideEffectRemedy'
                      ? { remedy: value, remedyOther: value === 'Other (અન્ય)' ? current.remedyOther || '' : '' }
                      : name === 'sideEffectRemedyOther'
                        ? { remedyOther: value }
                        : { reduced: value }),
            },
          },
        };
      });

      if (errors.sideEffectCycles?.[effect]) {
        setErrors((prev) => ({
          ...prev,
          sideEffectCycles: {
            ...prev.sideEffectCycles,
            [effect]: '',
          },
        }));
      }

      return;
    }

    if (type === 'checkbox' && name === 'sideEffects') {
      setForm((prev) => {
        const current = Array.isArray(prev[name]) ? prev[name] : [];

        const updated = checked
          ? [...current, value]
          : current.filter((item) => item !== value);

        const updatedCycles = { ...(prev.sideEffectCycles || {}) };

        if (checked && !updatedCycles[value]) {
          updatedCycles[value] = { cycle: '', other: '', frequency: '', remedy: '', remedyOther: '', reduced: '' };
        }

        if (!checked) {
          delete updatedCycles[value];
        }

        return {
          ...prev,
          [name]: updated,
          sideEffectCycles: updatedCycles,
        };
      });

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      }

      return;
    }

    const resolvedValue = type === 'select-multiple'
      ? Array.from(selectedOptions, (option) => option.value)
      : value;

    setForm((prev) => {
      const updated = {
        ...prev,
        [name]: resolvedValue,
      };

      if (name === 'diagnosis') {
        updated.drugRegimen = '';
        updated.sideEffects = [];
        updated.sideEffectCycles = {};
      }

      if (name === 'drugRegimen') {
        updated.sideEffects = [];
        updated.sideEffectCycles = {};
      }

      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  const validatePersonalInfo = useCallback(() => {
    const err = {};

    if (!form.name.trim()) {
      err.name = 'Full name is required';
    }

    if (!form.age.toString().trim()) {
      err.age = 'Age is required';
    } else if (Number(form.age) <= 0) {
      err.age = 'Enter a valid age';
    }

    if (!form.gender.trim()) {
      err.gender = 'Gender is required';
    }

    if (!form.bloodGroup.trim()) {
      err.bloodGroup = 'Blood group is required';
    }

    if (!form.phone.trim()) {
      err.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(form.phone.replace(/\s/g, ''))) {
      err.phone = 'Enter valid 10 digit number';
    }

    if (!form.regNo.trim()) {
      err.regNo = 'Hospital registration number is required';
    }

    if (!form.address.trim()) {
      err.address = 'Address is required';
    }

    return err;
  }, [form]);

  const validateMedicalInfo = useCallback(() => {
    const err = {};

    if (!form.diagnosis.trim()) {
      err.diagnosis = 'Diagnosis is required';
    }

    return err;
  }, [form]);

  const validateCycleInfo = useCallback(() => {
    const err = {};
    const sideEffectCycles = {};
    const selectedEffects = Array.isArray(form.sideEffects) ? form.sideEffects : [];

    selectedEffects.forEach((effect) => {
      const cycleDetails = form.sideEffectCycles?.[effect] || {};

      if (!cycleDetails.cycle) {
        sideEffectCycles[effect] = 'Cycle is required';
      } else if (cycleDetails.cycle === 'Other' && !cycleDetails.other?.trim()) {
        sideEffectCycles[effect] = 'Enter cycle manually';
      } else if (!cycleDetails.frequency) {
        sideEffectCycles[effect] = 'Count is required';
      } else if (!cycleDetails.remedy) {
        sideEffectCycles[effect] = 'Remedy is required';
      } else if (cycleDetails.remedy === 'Other (અન્ય)' && !cycleDetails.remedyOther?.trim()) {
        sideEffectCycles[effect] = 'Specify remedy manually';
      } else if (!cycleDetails.reduced) {
        sideEffectCycles[effect] = 'Please indicate if side effect reduced';
      }
    });

    if (Object.keys(sideEffectCycles).length > 0) {
      err.sideEffectCycles = sideEffectCycles;
    }

    return err;
  }, [form]);

  const submitForm = useCallback(async () => {
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
  }, [form, onSubmit]);

  const value = {
    patient,
    form,
    errors,
    setErrors,
    consent,
    setConsent,
    submitting,
    selectedDrugRegimens,
    selectedSideEffects,
    handleChange,
    validatePersonalInfo,
    validateMedicalInfo,
    validateCycleInfo,
    submitForm,
  };

  return (
    <PatientFormContext.Provider value={value}>
      {children}
    </PatientFormContext.Provider>
  );
}
